import { useEffect, useState } from "react";
import { Bar, BarChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import api from "../../lib/api";

const COLORS = ["#FF5A1F", "#1D4ED8", "#059669", "#D97706", "#64748B", "#94A3B8", "#EF4444"];

const card = "rounded-2xl border border-adminborder bg-admincard p-5";

function Kpi({ label, value, testid, accent }) {
  return (
    <div data-testid={testid} className={card}>
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`mt-2 font-heading text-2xl font-extrabold tracking-tight ${accent || "text-white"}`}>{value}</p>
    </div>
  );
}

function StatusPill({ label, at, empty = "Never" }) {
  return (
    <span className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-semibold ${at ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400" : "border-amber-500/30 bg-amber-500/10 text-amber-400"}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${at ? "bg-emerald-400" : "bg-amber-400"}`} />
      {label}: {at ? new Date(at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" }) : empty}
    </span>
  );
}

function CfgPill({ label, ok }) {
  return (
    <span className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-semibold ${ok ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400" : "border-red-500/30 bg-red-500/10 text-red-400"}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${ok ? "bg-emerald-400" : "bg-red-400"}`} />
      {label} {ok ? "Connected" : "Missing"}
    </span>
  );
}

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  const [sys, setSys] = useState(null);
  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setD(r.data)).catch(() => {});
    api.get("/admin/system-status").then((r) => setSys(r.data)).catch(() => {});
  }, []);

  if (!d) return <p className="py-20 text-center text-slate-500" data-testid="admin-dashboard-loading">Loading control center…</p>;

  return (
    <div data-testid="admin-dashboard" className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">Control Center</h1>
        <p className="mt-1 text-sm text-slate-500">Network-wide logistics overview</p>
      </div>

      {sys && (
        <div className="flex flex-wrap items-center gap-2" data-testid="admin-system-status">
          <StatusPill label="17TRACK Sync" at={sys.track17_last_sync} />
          <StatusPill label="17TRACK Webhook" at={sys.track17_last_webhook} empty="No pushes yet" />
          <CfgPill label="Courier API" ok={sys.integrations.shipmozo} />
          <CfgPill label="17TRACK API" ok={sys.integrations.track17} />
          <CfgPill label="Razorpay" ok={sys.integrations.razorpay} />
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi label="Revenue" value={`₹${d.revenue.toLocaleString("en-IN")}`} testid="admin-kpi-revenue" />
        <Kpi label="Margin (markup)" value={`₹${d.margin.toLocaleString("en-IN")}`} testid="admin-kpi-margin" accent="text-emerald-400" />
        <Kpi label="Total Shipments" value={d.total_shipments} testid="admin-kpi-shipments" />
        <Kpi label="Today's Bookings" value={d.todays_bookings} testid="admin-kpi-today" accent="text-saffron" />
      </div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi label="Delivered" value={d.delivered} accent="text-emerald-400" testid="admin-kpi-delivered" />
        <Kpi label="NDR / RTO" value={`${d.ndr} / ${d.rto}`} accent="text-amber-400" testid="admin-kpi-ndr-rto" />
        <Kpi label="Domestic / Intl" value={`${d.domestic} / ${d.international}`} testid="admin-kpi-dom-intl" />
        <Kpi label="Customers / Franchises" value={`${d.customers} / ${d.franchises}`} testid="admin-kpi-cust-fr" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className={card}>
          <h3 className="font-heading text-lg font-bold text-white">Bookings (14 days)</h3>
          <div className="mt-4 h-60" data-testid="admin-chart-bookings">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.by_day}>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={(x) => x.slice(5)} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#64748B" }} width={24} />
                <Tooltip contentStyle={{ background: "#111827", border: "1px solid #1F2937", borderRadius: 8 }} />
                <Bar dataKey="count" fill="#FF5A1F" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className={card}>
          <h3 className="font-heading text-lg font-bold text-white">Courier Split</h3>
          <div className="mt-4 h-60" data-testid="admin-chart-couriers">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={d.by_courier} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={3}>
                  {d.by_courier.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#111827", border: "1px solid #1F2937", borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 flex flex-wrap gap-3">
            {d.by_courier.map((c, i) => (
              <span key={c.name} className="flex items-center gap-1.5 text-xs text-slate-400">
                <span className="h-2 w-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{c.name} ({c.value})
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
